import { useEffect } from 'react';

const SITE_NAME = 'Wizbang';
const SITE_URL = 'https://www.wizbang.in';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png';
const TWITTER_HANDLE = '@wizbang_in';

export default function SEO({ 
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  article = null,
  noIndex = false,
  structuredData = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Building Revenue Systems`;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const defaultDescription = 'Wizbang is a tech-powered growth company combining content, performance advertising, and automation to build revenue systems for businesses.';
  const metaDescription = description || defaultDescription;
  const defaultKeywords = 'digital marketing, growth marketing, performance advertising, marketing automation, SEO, content marketing, lead generation, revenue systems';
  const metaKeywords = keywords || defaultKeywords;
  const safeImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMeta = (selector, content, attrName = 'content') => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        const [attr, value] = selector.replace(/[\[\]]/g, '').split('=');
        tag.setAttribute(attr.replace('meta', '').trim(), value?.replace(/"/g, '') || '');
        document.head.appendChild(tag);
      }
      tag.setAttribute(attrName, content);
    };

    // Update or create link tag
    const updateLink = (rel, href) => {
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // Basic meta tags
    updateMeta('meta[name="description"]', metaDescription);
    updateMeta('meta[name="keywords"]', metaKeywords);
    updateLink('canonical', fullUrl);

    // Robots
    const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    updateMeta('meta[name="robots"]', robotsContent);

    // Open Graph
    updateMeta('meta[property="og:type"]', type);
    updateMeta('meta[property="og:url"]', fullUrl);
    updateMeta('meta[property="og:title"]', fullTitle);
    updateMeta('meta[property="og:description"]', metaDescription);
    updateMeta('meta[property="og:image"]', safeImage);
    updateMeta('meta[property="og:site_name"]', SITE_NAME);
    updateMeta('meta[property="og:locale"]', 'en_IN');

    // Twitter
    updateMeta('meta[name="twitter:card"]', 'summary_large_image');
    updateMeta('meta[name="twitter:site"]', TWITTER_HANDLE);
    updateMeta('meta[name="twitter:title"]', fullTitle);
    updateMeta('meta[name="twitter:description"]', metaDescription);
    updateMeta('meta[name="twitter:image"]', safeImage);

    // Article specific
    if (article) {
      if (article.publishedTime) updateMeta('meta[property="article:published_time"]', article.publishedTime);
      if (article.modifiedTime) updateMeta('meta[property="article:modified_time"]', article.modifiedTime);
      if (article.author) updateMeta('meta[property="article:author"]', article.author);
    }

    // Structured Data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-schema="structured-data"]');
      if (existingScript) existingScript.remove();
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'structured-data');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup
    return () => {
      const schemaScript = document.querySelector('script[data-schema="structured-data"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [fullTitle, metaDescription, metaKeywords, fullUrl, noIndex, type, safeImage, article, structuredData]);

  return null; // This component only manages head tags via useEffect
}

// Pre-built structured data generators
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Wizbang",
  "alternateName": "Wizbang Digital",
  "url": "https://www.wizbang.in",
  "logo": DEFAULT_IMAGE,
  "description": "Tech-powered growth company combining content, performance advertising, and automation to build revenue systems.",
  "email": "sayhi@wizbang.in",
  "telephone": "+919878780298",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IN"
  },
  "sameAs": [
    "https://www.linkedin.com/company/wizbang",
    "https://twitter.com/wizbang_in"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+919878780298",
    "contactType": "sales",
    "email": "sayhi@wizbang.in",
    "availableLanguage": ["English", "Hindi"]
  }
});

export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Wizbang",
  "image": DEFAULT_IMAGE,
  "url": "https://www.wizbang.in",
  "telephone": "+919878780298",
  "email": "sayhi@wizbang.in",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IN"
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  }
});

export const generateServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.name,
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "Organization",
    "name": "Wizbang",
    "url": "https://www.wizbang.in"
  },
  "areaServed": {
    "@type": "Country",
    "name": "India"
  }
});

export const generateBlogPostSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": post.title,
  "description": post.excerpt,
  "image": post.image,
  "datePublished": post.publishedDate,
  "dateModified": post.modifiedDate || post.publishedDate,
  "author": {
    "@type": "Person",
    "name": post.author
  },
  "publisher": {
    "@type": "Organization",
    "name": "Wizbang",
    "logo": {
      "@type": "ImageObject",
      "url": DEFAULT_IMAGE
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://www.wizbang.in/blog/${post.slug}`
  }
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://www.wizbang.in${item.path}`
  }))
});

export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Wizbang",
  "url": "https://www.wizbang.in",
  "description": "Tech-powered growth company building revenue systems",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://www.wizbang.in/blog?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});
